import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { useLazyQuery } from "@apollo/client";
import {
  GET_EMAIL_BY_ID,
  QUERY_CLIENT_PHOTOS,
} from "../../../../utils/GraphQL/queries";
import "./emailModal.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Buffer } from "buffer";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
// import { copyImageToClipboard } from "copy-image-clipboard";
import copyImageToClip from "../../../../utils/copyImageToClip";
import fetchImageForCanvas from "../../../../utils/fetchImageForCanvas";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ImageModal from "../ImageModal/ImageModal";
import { useNavigate } from "react-router";
import SvgChevronLeft from "../SvgChevrons/SvgChevronLeft";
import SvgChevronRight from "../SvgChevrons/SvgChevronRight";

/*
 * `layer` (2026-10-05): this modal is now opened from INSIDE another modal —
 * the Tasks console's quote-requests list. Two BdrModals both defaulting to
 * `base` land in one z-band ordered only by DOM insertion, which is exactly
 * what the overlay ladder exists to prevent, so the host passes a rung and it
 * is forwarded to BOTH this modal and the ImageModal it can open.
 *
 * `prepareReply` is OPTIONAL now, for the same reason: a host that has nowhere
 * to put a reply composer passes none, and the Reply button is hidden rather
 * than throwing on click.
 */
const EmailModal = ({
  emailId,
  closeEmail,
  prepareReply,
  /* Optional, for the same reason as `prepareReply`: a host with no email
     LIST behind it cannot navigate to a sibling message. */
  changeEmail,
  layer = "base",
  imageLayer = "raised",
}) => {
  const navigate = useNavigate();
  const [dateFormat, setDateFormat] = useState("");
  // const [addedCopyListeners, setAddedCopyListeners] = useState(false);
  const [imgIds, setImgIds] = useState([]);
  // const [btnIdsForCopy, setBtnIdsForCopy] = useState([]);
  // const [imgIdsForCopy, setImgIdsForCopy] = useState([]);
  const htmlRef = useRef();
  const boxRefForSvgs = useRef(null);
  const [loadEmail, { loading: emailLoading }] = useLazyQuery(GET_EMAIL_BY_ID);
  const [getIncImgIdData, { loading: imageLoading }] =
    useLazyQuery(QUERY_CLIENT_PHOTOS);
  const [imgData, setImgData] = useState([]);
  const [email, setEmail] = useState({});
  const [isCurrentEmailSent, setIsCurrentEmailSent] = useState(false);
  const [clientNavButtonId, setClientNavButtonId] = useState("");
  const [emailChain, setEmailChain] = useState([]);
  const [newerEmailId, setNewerEmailId] = useState("");
  const [attachImgs, setAttachImgData] = useState([]);
  const [getEmailChain, setGetEmailChain] = useState(false);
  const [moreInChain, setMoreInChain] = useState(false);
  const [svgBoxDim, setSvgBoxDim] = useState({ h: "0", w: "0" });
  const [attachPdf, setAttachPdf] = useState([]);
  const { handleSnack } = useSnackbar();
  const [imageModal, setImageModal] = useState({
    open: false,
    src: "",
  });

  useEffect(() => {
    getImgData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgIds]);

  useEffect(() => {
    if (boxRefForSvgs?.current) {
      const height = boxRefForSvgs.current?.offsetHeight;

      setSvgBoxDim({ h: `${height}`, w: `${height}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxRefForSvgs?.current]);

  useEffect(() => {
    setEmail({});
    setEmailChain([]);
    fetchEmail(emailId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => {
    if (getEmailChain) {
      fetchEmail(emailId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEmailChain]);

  useEffect(() => {
    const chainCombined = emailChain?.length ? [...emailChain] : [{ ...email }];
    let imgObjArr = [];
    let pdfArr = [];

    chainCombined?.forEach((obj) => {
      if (obj?.attachImgs?.length > 0) {
        for (let index = 0; index < obj?.attachImgs?.length; index++) {
          const element = obj?.attachImgs[index];
          imgObjArr?.push(element);
        }
      }

      if (obj?.attachPdf?.length > 0) {
        for (let index = 0; index < obj?.attachPdf?.length; index++) {
          const element = obj?.attachPdf[index];
          pdfArr?.push(element);
        }
      }
      return;
    });

    if (pdfArr?.length > 0) {
      setAttachPdf([...pdfArr]);
    }

    if (imgObjArr?.length > 0) {
      // No `originalData` copy: the S3 read-swap means these carry no base64,
      // and rotate re-derives from `s3ImageUrl` rather than a stored original.
      setAttachImgData([...imgObjArr]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, emailChain]);

  useEffect(() => {
    setImgIds([]);
    if (email?.html || email?.emailText || email?.emailHTML) {
      filterImgIdsInHtml();
    }

    let date = "";
    const getNum = parseInt(email?.dateCreated);
    if (getNum > 0) {
      date = BAGroupTimeFormat(getNum, true);
    }
    setDateFormat(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const rotateImg = async (index, rotation, attachedBool) => {
    const next = index + 1;
    const rot = rotation + 90;

    // Source the ORIGINAL bytes from S3 instead of base64 — keeps rotate
    // client-side/ephemeral and off the server-base64 path. The cumulative
    // angle still re-derives from the original.
    //
    // Loaded through fetchImageForCanvas (CORS fetch, no-store, blob url), NOT
    // a crossOrigin Image: the tile displays this same url as a plain <img>,
    // which caches a response without CORS headers that a crossOrigin load of
    // the same url would then fail on (see fetchImageForCanvas.js).
    const src = attachedBool
      ? attachImgs[index]?.s3ImageUrl
      : imgData[index]?.s3ImageUrl;

    let img;
    try {
      img = await fetchImageForCanvas(src);
    } catch (err) {
      console.error("[EmailModal.rotateImg] image load failed", { src, err });
      handleSnack("Couldn't load the image to rotate", 2500);
      return;
    }

    let canvas = document.createElement("canvas");

    const wide = rot === 90 || rot === 270 ? img.height : img.width;
    const high = rot === 90 || rot === 270 ? img.width : img.height;

    canvas.width = wide;
    canvas.height = high;
    let ctx = canvas.getContext("2d");

    const drawH = rot === 90 || rot === 270 ? high : wide;
    const drawW = rot === 90 || rot === 270 ? wide : high;

    ctx.translate(wide / 2, high / 2);
    ctx.rotate(rot * (Math.PI / 180));
    ctx.drawImage(img, -drawH / 2, -drawW / 2);

    if (attachedBool) {
      setAttachImgData((prev) => [
        ...prev.slice(0, index),
        {
          ...prev[index],
          imageData: canvas.toDataURL("image/png", 1),
          rotation: rot,
        },
        ...prev.slice(next),
      ]);

      return;
    }

    if (!attachedBool) {
      setImgData((prev) => [
        ...prev.slice(0, index),
        {
          ...prev[index],
          imageData: canvas.toDataURL("image/png", 1),
          rotation: rot,
        },
        ...prev.slice(next),
      ]);
    }
  };

  const handleImgModalClose = () => {
    setImageModal((prev) => ({
      open: false,
      src: prev?.src,
    }));
  };

  const filterImgIdsInHtml = () => {
    let html = email?.html || email?.emailHTML || "";
    let text = email?.emailText || "";

    const regexBGWhite =
      /((background:|backgroundColor:|background-color:)\s?(white|#fff(?:fff)?|rgba?\(\s*255\s*,\s*255\s*,\s*255\s*(?:,\s*1\s*)?\));?)/gi;

    html = html.replace(
      regexBGWhite,
      "background: #525151; color: #fff !important;"
    );

    text = text.replace(
      regexBGWhite,
      "background: #525151; color: #fff !important;"
    );

    const newLineRegex = /\t|\n|\r/g;
    text = text.replaceAll(newLineRegex, "<br />");

    const htmlArr = html?.length > 0 ? html?.split(" ") : [];
    // const textArr = text?.split(" ") || [];

    htmlArr.forEach((word) => {
      const idRegEx = /^[a-f\d]{24}$/gim;
      if (idRegEx.test(word)) {
        setImgIds((prev) => {
          const newIds = prev.filter((id) => id !== word);
          return [...newIds, word];
        });
      }
      return;
    });

    // textArr.forEach((word) => {
    //   const idRegEx = /^[a-f\d]{24}$/gmi;
    //   if (idRegEx.test(word)) {
    //     const test = word.match(idRegEx);
    //     setImgIds((prev) => {
    //       const newIds = prev.filter((id) => id !== word);
    //       return [...newIds, word];
    //     });
    //   }
    //   return;
    // });

    const setupHtml = htmlArr.join(" ");
    let newHtml = null;
    let clientId = null;
    const clientIdRegEx = /%%-[a-f\d]{24}-%%/gim;
    const clientIdArr = setupHtml?.match(clientIdRegEx);
    if (clientIdArr) {
      const id = clientIdArr[0]?.split("-")[1];
      clientId = id;
    }

    if (clientId) {
      setClientNavButtonId(clientId);
      newHtml = setupHtml.replace(/%%-[a-f\d]{24}-%%/gim, "");
    }

    if (htmlRef?.current) {
      const rawHtml = newHtml
        ? newHtml
        : htmlArr?.length > 0
        ? htmlArr.join(" ")
        : text;
      htmlRef.current.innerHTML = DOMPurify.sanitize(rawHtml);
    }
  };

  const getImgData = async () => {
    const { data } = await getIncImgIdData({
      variables: { pictureIdArr: imgIds },
    });

    if (data?.getImageById?.length > 0) {
      setImgData([...data.getImageById]);
    }
  };

  const fetchEmail = async (id) => {
    const { data } = await loadEmail({
      variables: {
        emailId: id,
        getMore: getEmailChain,
      },
      fetchPolicy: "network-only",
    });

    let currentEmail = null;

    if (
      data?.getEmailById?.currentEmail?.incEmail ||
      data?.getEmailById?.currentEmail?.sentEmail
    ) {
      currentEmail = data?.getEmailById?.currentEmail?.sentEmail
        ? data?.getEmailById?.currentEmail?.sentEmail
        : data?.getEmailById?.currentEmail?.incEmail;

      const isCurrentEmailSentBool = currentEmail?.__typename === "SentEmail";

      setIsCurrentEmailSent(isCurrentEmailSentBool);
      setEmail({ ...currentEmail });
    }

    if (!currentEmail) {
      handleSnack("Couldn't fetch email at this time...Please refresh and try again.")
      return null;
    }

    // returns minimal data that a chain exists
    if (data?.getEmailById?.moreInChain) {
      setMoreInChain(data?.getEmailById?.moreInChain);
    }

    // returns newer email id without chain needing to be present
    if (data?.getEmailById?.newerId) {
      setNewerEmailId(data?.getEmailById?.newerId);
    }

    // Combine Sent and Chained emails for sorting and newer check
    const sentAndChainedBeforeNewerCheck = [
      ...data?.getEmailById?.emailChain,
      ...data?.getEmailById?.emailSentChain,
    ];

    // Will set display for chain if called on
    if (sentAndChainedBeforeNewerCheck?.length > 0) {
      //set bool if there are older or newer emails
      const olderAndNotSentEmails = [];
      const hasNewer = sentAndChainedBeforeNewerCheck?.filter((item) => {
        const bdrRegex = /@bigginsdoorrefinishing.com/gim;
        // Doesn't allow sent email id to be considered newer email from client
        if (bdrRegex.test(item?.from)) {
          olderAndNotSentEmails?.push(item);
          return null;
        }

        if (parseInt(item?.dateCreated) < parseInt(currentEmail?.dateCreated)) {
          olderAndNotSentEmails?.push(item);
          return null;
        }

        return item.id;
      });

      if (hasNewer?.length > 0) {
        setNewerEmailId(hasNewer[0]);
      } else {
        setNewerEmailId("");
      }

      // newest first
      const allEmails = [{ ...currentEmail }, ...olderAndNotSentEmails].sort(
        (a, b) => {
          return a.dateCreated < b.dateCreated ? 1 : -1;
        }
      );

      setEmailChain([...allEmails]);
    }

    if (
      data?.getEmailById?.emailChain?.length === 0 &&
      data?.getEmailById?.emailSentChain?.length === 0 &&
      !data?.getEmailById?.newerId &&
      !data?.getEmailById?.moreInChain
    ) {
      setEmailChain([]);
      setNewerEmailId("");
    }
  };

  return (
    <>
      {(emailLoading || imageLoading) && (
        <AdminBDRSpinner message={"Loading..."} />
      )}
      <ImageModal
        modalOpen={imageModal?.open}
        handleClose={handleImgModalClose}
        imgSrc={imageModal?.src}
        layer={imageLayer}
      />
      <BdrModal
        open
        onClose={closeEmail}
        size="lg"
        variant="panel"
        title="Email"
        layer={layer}
        actions={<BdrButton onClick={closeEmail}>Close</BdrButton>}
      >

          {emailChain?.length <= 0 && (
            <ul className="email-modal-list-older">
              <li className="email-modal-list-hdr-bg">
                <div>
                  <span className="green-color-text">From:</span> {email?.from}
                </div>
                <div>
                  <span className="green-color-text">Subject:</span>{" "}
                  {email?.emailSubject}
                </div>
                <span>
                  <span className="green-color-text">To:</span> {email?.to}
                </span>
                <span>
                  <span className="green-color-text">Date:</span> {dateFormat}
                </span>
                <div>
                  {/* Hidden rather than throwing when the host has nowhere to
                      mount a reply composer. */}
                  {prepareReply && (
                    <BdrButton
                      variant="action"
                      onClick={() => {
                        prepareReply(email, isCurrentEmailSent);
                      }}
                    >
                      Reply
                    </BdrButton>
                  )}
                  {clientNavButtonId && (
                    <BdrButton
                      onClick={() => {
                        navigate(
                          `/bdr-office/clients/view-client/${clientNavButtonId}`
                        );
                      }}
                      variant="action"
                    >
                      View Client
                    </BdrButton>
                  )}
                  {changeEmail && newerEmailId && (
                    <BdrButton
                      onClick={(event) => {
                        event.preventDefault();
                        setNewerEmailId("");
                        changeEmail(newerEmailId);
                      }}
                      variant="action"
                      className="email-modal-older-btn"
                    >
                      View Newer Email
                    </BdrButton>
                  )}
                </div>
              </li>
              <li ref={htmlRef} className="email-modal-body-html"></li>
            </ul>
          )}

          {moreInChain && emailChain?.length <= 0 && (
            <BdrButton
              onClick={() => {
                setGetEmailChain(true);
              }}
              variant="action"
              className="email-modal-older-btn"
            >
              View Prev Emails
            </BdrButton>
          )}

          {emailChain?.length > 0 && (
            <ul className="email-modal-list-older">
              <li className="email-modal-list-hdr-bg">
                <div>
                  {isCurrentEmailSent && (
                    <span className="green-color-text">To: {email?.to}</span>
                  )}
                  {!isCurrentEmailSent && (
                    <span className="green-color-text">
                      From: {email?.from}
                    </span>
                  )}
                </div>
                <div>
                  <span className="green-color-text">Subject:</span>{" "}
                  {email?.emailSubject}
                </div>
                <div>
                  {prepareReply && (
                    <BdrButton
                      variant="action"
                      onClick={() => {
                        prepareReply(email, isCurrentEmailSent);
                      }}
                    >
                      Reply
                    </BdrButton>
                  )}
                  {clientNavButtonId && (
                    <BdrButton
                      onClick={() => {
                        navigate(
                          `/bdr-office/clients/view-client/${clientNavButtonId}`
                        );
                      }}
                      variant="action"
                    >
                      View Client
                    </BdrButton>
                  )}
                  {changeEmail && newerEmailId && (
                    <BdrButton
                      onClick={(event) => {
                        event.preventDefault();
                        setNewerEmailId("");
                        changeEmail(newerEmailId);
                      }}
                      variant="action"
                      className="email-modal-older-btn"
                    >
                      View Newer Email
                    </BdrButton>
                  )}
                </div>
              </li>
            </ul>
          )}
          {emailChain?.length > 0 &&
            emailChain?.map((emailObj, index) => {
              const ref = index === 0 ? boxRefForSvgs : null;
              const id =
                index === 0
                  ? "first-of-older-chain"
                  : `${index}-of-older-chain`;

              let date = "";
              const getNum = parseInt(emailObj?.dateCreated);
              if (getNum > 0) {
                date = BAGroupTimeFormat(getNum, true);
              }

              const emailHtml = emailObj?.emailHTML
                ? emailObj?.emailHTML
                : emailObj?.html;

              const sent =
                svgBoxDim?.h !== "0" ? (
                  emailObj?.__typename === "SentEmail" ? (
                    <SvgChevronRight viewBoxInc={svgBoxDim} />
                  ) : (
                    <SvgChevronLeft viewBoxInc={svgBoxDim} />
                  )
                ) : (
                  ""
                );

              const delay =
                emailObj?.__typename === "SentEmail" ? [0, 1, 2] : [2, 1, 0];

              return (
                <ul
                  id={id}
                  key={`email-chain-id-${emailObj?.id}`}
                  className="email-modal-list-older"
                >
                  <li className="email-modal-list-older-hdr email-modal-list-hdr-bg">
                    <div className="email-modal-date-box-raise-z">
                      <span className="green-color-text">Date:</span> {date}
                    </div>
                    <div ref={ref} className="email-modal-chevron-box">
                      <div
                        className={`email-modal-chevron-box-inner fade-in-svg animation-delay-${delay[0]}`}
                      >
                        {sent}
                      </div>
                      <div
                        className={`email-modal-chevron-box-inner fade-in-svg animation-delay-${delay[1]}`}
                      >
                        {sent}
                      </div>
                      <div
                        className={`email-modal-chevron-box-inner fade-in-svg animation-delay-${delay[2]}`}
                      >
                        {sent}
                      </div>
                    </div>
                  </li>
                  {emailHtml && (
                    <li
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailHtml) }}
                      className="email-modal-body-html"
                    ></li>
                  )}
                </ul>
              );
            })}

          <div className="email-modal-images-container">
            {imgData &&
              imgData?.map((item, index) => {
                const imgId = "imgEl" + item._id;
                const btnId = "btnEl" + item._id;

                const html = (
                  <div
                    key={item._id + index}
                    className="email-modal-images-box"
                  >
                    <div className="email-modal-img-overlay-box">
                      <BdrButton
                        size="sm"
                        variant="action"
                        iconOnly
                        icon={<ContentCopyIcon fontSize="inherit" />}
                        ariaLabel="Copy image"
                        title="Copy image"
                        id={btnId}
                        onPointerUp={async () => {
                          const img = document.querySelector(`#${imgId}`);
                          const blob = new ClipboardItem({
                            "image/png": copyImageToClip(img.src),
                          });

                          navigator.clipboard
                            .write([blob])
                            .then(() => handleSnack("Image copied", 1500))
                            .catch((err) => {
                              console.error("[EmailModal] copy image failed", {
                                src: img.src,
                                err,
                              });
                              handleSnack("Couldn't copy the image", 2500);
                            });
                        }}
                      />
                      <BdrButton
                        size="sm"
                        variant="action"
                        iconOnly
                        icon={<RotateRightIcon fontSize="inherit" />}
                        ariaLabel="Rotate image"
                        title="Rotate image"
                        onClick={() => {
                          const rotation =
                            item?.rotation === 360 ? 0 : item?.rotation || 0;
                          rotateImg(index, rotation, false);
                        }}
                      />{" "}
                    </div>
                    <img
                      id={imgId}
                      className="email-modal-img bdr-border-thin bdr-shadow-subtle"
                      src={item?.imageData ?? item?.s3ImageUrl}
                      alt={`door`}
                      onClick={() => {
                        setImageModal({
                          open: true,
                          src: item?.imageData ?? item?.s3ImageUrl,
                        });
                      }}
                    />
                  </div>
                );

                return html;
              })}
            {(attachImgs?.length > 0 || attachPdf?.length > 0) && (
              <>
                {attachImgs?.length > 0 &&
                  attachImgs?.map((item, index) => {
                    const imgId = "attImgEl" + item._id;
                    const btnId = "attBtnEl" + item._id;

                    const html = (
                      <div
                        key={item._id + index}
                        className="email-modal-images-box"
                      >
                        <div className="email-modal-img-overlay-box">
                          <BdrButton
                            size="sm"
                            variant="action"
                            iconOnly
                            icon={<ContentCopyIcon fontSize="inherit" />}
                            ariaLabel="Copy image"
                            title="Copy image"
                            id={btnId}
                            onPointerUp={async () => {
                              const img = document.querySelector(`#${imgId}`);
                              const blob = new ClipboardItem({
                                "image/png": copyImageToClip(img.src),
                              });

                              navigator.clipboard
                                .write([blob])
                                .then(() => handleSnack("Image copied", 1500))
                                .catch((err) => {
                                  console.error(
                                    "[EmailModal] copy attachment failed",
                                    { src: img.src, err }
                                  );
                                  handleSnack("Couldn't copy the image", 2500);
                                });
                            }}
                          />
                          <BdrButton
                            size="sm"
                            variant="action"
                            iconOnly
                            icon={<RotateRightIcon fontSize="inherit" />}
                            ariaLabel="Rotate image"
                            title="Rotate image"
                            onClick={() => {
                              const rotation =
                                item?.rotation === 360
                                  ? 0
                                  : item?.rotation || 0;
                              rotateImg(index, rotation, true);
                            }}
                          />{" "}
                        </div>
                        <img
                          id={imgId}
                          className="email-modal-img bdr-border-thin bdr-shadow-subtle"
                          src={item?.imageData ?? item?.s3ImageUrl}
                          alt={`door pic ${index}`}
                          onClick={() => {
                            setImageModal({
                              open: true,
                              src: item?.imageData ?? item?.s3ImageUrl,
                            });
                          }}
                        />
                      </div>
                    );

                    return html;
                  })}
                {attachPdf?.length > 0 &&
                  attachPdf?.map((item, index) => {
                    const html = (
                      <div
                        key={item?.id + index}
                        className="email-modal-pdf-box"
                      >
                        <div>
                          <BdrButton
                            size="sm"
                            variant="action"
                            onClick={() => {
                              // Prefer the direct (signed) S3 URL — off the
                              // server-base64 path; fall back to the legacy
                              // base64->blob for any un-migrated PDF.
                              if (item?.s3FileUrl) {
                                window.open(item.s3FileUrl);
                                return;
                              }
                              // `GET_EMAIL_BY_ID` no longer selects `fileData`
                              // (dropped in the S3 read-swap), so this branch
                              // only runs for a PDF with no `s3FileUrl` — and
                              // it has no bytes to decode. Guard instead of
                              // throwing on `undefined.split`.
                              if (typeof item?.fileData !== "string") {
                                handleSnack(
                                  "PDF unavailable — this attachment has not been migrated to S3",
                                  3000
                                );
                                return;
                              }
                              const destructData = item.fileData.split(";");
                              // base64 string
                              const base64str = destructData[1].split(",")[1];
                              const pdfData = Buffer.from(base64str, "base64");

                              // create the blob object with content-type "application/pdf"
                              let blob = new Blob([pdfData], {
                                type: "application/pdf",
                              });
                              let url = URL.createObjectURL(blob);

                              window.open(url);
                            }}
                          >
                            View PDF {index + 1}
                          </BdrButton>
                        </div>
                      </div>
                    );

                    return html;
                  })}
              </>
            )}
          </div>
      </BdrModal>
    </>
  );
};

export default EmailModal;
